import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { ContentActionsMenu, EditedLabel } from "../components/ContentActionsMenu";
import { ThreadItem } from "../components/ThreadItem";
import { Link } from "react-router-dom";
import {
  Plus, Heart, MessageCircle, BookOpen, Film, Gamepad2,
  Mountain, Palette, Send, EyeOff, Shield, ArrowRight,
  Briefcase, CalendarDays, MapPin, Users, Clock, Sparkles, Tag
} from "lucide-react";

// Meta for built-in categories; custom ones fall back to Sparkles + rotating color
const DEFAULT_CAT_META = {
  book_club: { icon: BookOpen, color: "#8DB5A0" },
  movies_tv: { icon: Film, color: "#8DB5A0" },
  gaming: { icon: Gamepad2, color: "#6366F1" },
  outdoors: { icon: Mountain, color: "#10B981" },
  hobbies: { icon: Palette, color: "#8DB5A0" },
};
const CUSTOM_COLORS = ["#8B5CF6", "#EC4899", "#F59E0B", "#14B8A6", "#F97316", "#06B6D4", "#84CC16"];
const colorForKey = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return CUSTOM_COLORS[hash % CUSTOM_COLORS.length];
};

const CommunityPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]); // [{key, label, created_by?}]
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState("");
  const [commentAnon, setCommentAnon] = useState(false);
  const [form, setForm] = useState({ content: "", category: "book_club", is_anonymous: false });
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "", description: "", category: "book_club",
    event_date: "", event_time: "", location: "", is_online: false, max_attendees: ""
  });

  // Meta lookup merges built-in + custom (Sparkles + deterministic color)
  const catMetaFor = useMemo(() => (key) => {
    if (DEFAULT_CAT_META[key]) return DEFAULT_CAT_META[key];
    return { icon: Sparkles, color: colorForKey(key || "x") };
  }, []);
  const labelFor = useMemo(() => (key) => {
    const found = categories.find(c => c.key === key);
    return found ? found.label : key;
  }, [categories]);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchPosts(); fetchEvents(); }, [selectedCat]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/community/categories`);
      setCategories(res.data.categories || []);
    } catch { /* silent */ }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = selectedCat ? `?category=${selectedCat}` : "";
      const res = await axios.get(`${API}/community/posts${params}`);
      setPosts(res.data);
    } catch { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    try {
      const params = selectedCat ? `?category=${selectedCat}` : "";
      const res = await axios.get(`${API}/community/events${params}`);
      setEvents(res.data);
    } catch {}
  };

  const EMPTY_POST = { content: "", category: "book_club", is_anonymous: false };
  const EMPTY_EVENT = { title: "", description: "", category: "book_club", event_date: "", event_time: "", location: "", is_online: false, max_attendees: "" };

  const setPostDialog = (open) => {
    setShowCreate(open);
    if (!open) { setEditingPostId(null); setForm(EMPTY_POST); }
  };
  const setEventDialog = (open) => {
    setShowCreateEvent(open);
    if (!open) { setEditingEventId(null); setEventForm(EMPTY_EVENT); }
  };

  const startEditPost = (post) => {
    setEditingPostId(post.post_id);
    setForm({ content: post.content, category: post.category, is_anonymous: !!post.is_anonymous });
    setShowCreate(true);
  };
  const startEditEvent = (ev) => {
    setEditingEventId(ev.event_id);
    setEventForm({
      title: ev.title || "", description: ev.description || "", category: ev.category,
      event_date: ev.event_date || "", event_time: ev.event_time || "", location: ev.location || "",
      is_online: !!ev.is_online, max_attendees: ev.max_attendees ? String(ev.max_attendees) : "",
    });
    setShowCreateEvent(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error("Write something!"); return; }
    setSubmitting(true);
    if (editingPostId) {
      try {
        const res = await axios.put(`${API}/community/posts/${editingPostId}`, { content: form.content, category: form.category });
        if (res.data.status === "hidden") {
          toast.warning("Your edit was flagged for review and is hidden until an admin approves it.");
          setPosts(prev => prev.filter(p => p.post_id !== editingPostId));
        } else {
          toast.success("Post updated");
          setPosts(prev => prev.map(p => p.post_id === editingPostId ? { ...p, ...res.data } : p));
        }
        setPostDialog(false);
      } catch (error) { toast.error(error.response?.data?.detail || "Failed to update"); }
      finally { setSubmitting(false); }
      return;
    }
    try {
      const res = await axios.post(`${API}/community/posts`, form);
      if (res.data.redirect) {
        toast.info(res.data.message, { duration: 5000 });
      } else if (res.data.message) {
        toast.warning(res.data.message);
        setShowCreate(false);
        setForm({ content: "", category: "book_club", is_anonymous: false });
      } else {
        toast.success("Posted!");
        setShowCreate(false);
        setForm({ content: "", category: "book_club", is_anonymous: false });
        fetchPosts();
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.post_id === postId ? {
        ...p, is_liked: res.data.liked, like_count: p.like_count + (res.data.liked ? 1 : -1)
      } : p));
    } catch { toast.error("Failed"); }
  };

  const handleExpand = async (postId) => {
    if (expandedPost === postId) { setExpandedPost(null); return; }
    setExpandedPost(postId);
    if (!comments[postId]) {
      try {
        const res = await axios.get(`${API}/community/posts/${postId}/comments`);
        setComments(prev => ({ ...prev, [postId]: res.data }));
      } catch {}
    }
  };

  const handleComment = async (postId) => {
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/comments`, { content: commentText, is_anonymous: commentAnon });
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, comment_count: p.comment_count + 1 } : p));
      setCommentText("");
      setCommentAnon(false);
      if (res.data.is_anonymous) toast.success(`Comment posted as ${res.data.author_name}`);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to comment"); }
  };

  const saveComment = async (postId, c, content) => {
    try {
      const res = await axios.put(`${API}/community/posts/${postId}/comments/${c.comment_id}`, { content });
      setComments(prev => ({ ...prev, [postId]: prev[postId].map(x => x.comment_id === c.comment_id ? { ...x, ...res.data } : x) }));
      toast.success("Comment updated");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update"); throw error; }
  };

  const deleteComment = async (postId, c) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}/comments/${c.comment_id}`);
      setComments(prev => ({ ...prev, [postId]: prev[postId].filter(x => x.comment_id !== c.comment_id) }));
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, comment_count: Math.max(0, p.comment_count - 1) } : p));
      toast.success("Comment deleted");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`);
      toast.success("Post deleted");
      setPosts(prev => prev.filter(p => p.post_id !== postId));
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/community/events/${eventId}`);
      toast.success("Event deleted");
      setEvents(prev => prev.filter(ev => ev.event_id !== eventId));
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.event_date) { toast.error("Title and date required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...eventForm, max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null };
      if (editingEventId) {
        await axios.put(`${API}/community/events/${editingEventId}`, payload);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/community/events`, payload);
        toast.success("Event created!");
      }
      setEventDialog(false);
      fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const handleRSVP = async (eventId) => {
    try {
      const res = await axios.post(`${API}/community/events/${eventId}/rsvp`);
      toast.success(res.data.attending ? "You're in!" : "RSVP cancelled");
      fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const label = newCategoryName.trim();
    if (!label) { toast.error("Please enter a category name"); return; }
    if (label.length > 40) { toast.error("Category name must be 40 characters or less"); return; }
    setCreatingCategory(true);
    try {
      const res = await axios.post(`${API}/community/categories`, { label });
      setCategories(prev => [...prev, res.data]);
      setSelectedCat(res.data.key);
      setForm(f => ({ ...f, category: res.data.key }));
      setEventForm(f => ({ ...f, category: res.data.key }));
      toast.success(`Category "${res.data.label}" created!`);
      setShowCreateCategory(false);
      setNewCategoryName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create category");
    } finally {
      setCreatingCategory(false);
    }
  };

  const timeAgo = (d) => {
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="community-page">
        <Tabs defaultValue="social" className="space-y-6">
          {/* Header with tabs */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <EditableText
                blockKey="community.title"
                fallback="Community"
                as="h1"
                className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              />
              <EditableText
                blockKey="community.subtitle"
                fallback="Connect, share, and unwind with fellow hygienists"
                as="p"
                className="text-[#64748B] mt-1"
                multiline
              />
            </div>
            <div
              className="overflow-x-auto -mx-2 px-2 w-full min-w-0 scroll-smooth"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <TabsList className="bg-[#F0ECF7] inline-flex flex-nowrap w-max self-start">
                <TabsTrigger value="social" className="data-[state=active]:bg-white shrink-0" data-testid="social-feed-tab">Social Feed</TabsTrigger>
                <TabsTrigger value="events" className="data-[state=active]:bg-white shrink-0" data-testid="events-tab">Events</TabsTrigger>
                <TabsTrigger value="professional" className="data-[state=active]:bg-white shrink-0" data-testid="professional-feed-tab">Professional</TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Social Feed */}
          <TabsContent value="social" className="space-y-6">
            {/* Rules banner */}
            <div className="bg-[#6B4E8A] rounded-xl p-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#8DB5A0] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-white font-medium">Social Space</p>
                <p className="text-xs text-white/60">This is a work-free zone! No clinic, patient, or workplace talk. Just fun, connection, and good vibes.</p>
              </div>
            </div>

            {/* Category filter + New Post */}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
                <button onClick={() => setSelectedCat("")} className={`px-4 py-2 text-sm rounded-full border transition-colors shrink-0 ${!selectedCat ? 'bg-[#6B4E8A] text-white border-[#6B4E8A]' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`} data-testid="cat-filter-all">All</button>
                {categories.map((cat) => {
                  const meta = catMetaFor(cat.key);
                  const Icon = meta.icon;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCat(cat.key)}
                      className={`px-4 py-2 text-sm rounded-full border transition-colors shrink-0 flex items-center gap-1.5 ${selectedCat === cat.key ? 'text-white border-transparent' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`}
                      style={selectedCat === cat.key ? { backgroundColor: meta.color } : {}}
                      data-testid={`cat-filter-${cat.key}`}
                    >
                      <Icon className="w-3.5 h-3.5" />{cat.label}
                    </button>
                  );
                })}
                <button
                  onClick={() => setShowCreateCategory(true)}
                  className="px-3 py-2 text-sm rounded-full border border-dashed border-[#8DB5A0] text-[#8DB5A0] hover:bg-[#8DB5A0]/10 transition-colors shrink-0 flex items-center gap-1.5"
                  data-testid="create-category-btn"
                >
                  <Plus className="w-3.5 h-3.5" />Add Category
                </button>
              </div>
              <Dialog open={showCreate} onOpenChange={setPostDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full shrink-0" data-testid="new-social-post-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingPostId ? "Edit Post" : "Share with the Community"}</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>What's on your mind?</Label>
                      <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Share a book recommendation, a movie review, a hiking trail..." className="mt-2 bg-white border-[#E5DFEC] min-h-[120px]" data-testid="social-post-content" />
                    </div>
                    {!editingPostId && (
                      <div className="flex items-center space-x-2">
                        <Checkbox id="anon-social" checked={form.is_anonymous} onCheckedChange={c => setForm({ ...form, is_anonymous: c })} />
                        <Label htmlFor="anon-social" className="cursor-pointer text-sm">Post anonymously</Label>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setPostDialog(false)} className="rounded-full">Cancel</Button>
                      <Button type="submit" className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={submitting} data-testid="submit-social-post">{submitting ? "Saving..." : editingPostId ? "Save changes" : "Post"}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Posts Feed */}
            {loading ? (
              <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
            ) : posts.length > 0 ? (
              <div className="space-y-4">
                {posts.map((post, idx) => {
                  const meta = catMetaFor(post.category);
                  const Icon = meta.icon;
                  const isExpanded = expandedPost === post.post_id;
                  return (
                    <motion.div key={post.post_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }} className="bg-white border border-[#E5DFEC] rounded-2xl overflow-hidden">
                      <div className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shrink-0" style={{ backgroundColor: meta.color }}>
                            {post.is_anonymous ? <EyeOff className="w-4 h-4" /> : post.author_initial}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-[#6B4E8A]">{post.author_name}</span>
                              <span className="text-xs text-[#64748B]">{timeAgo(post.created_at)}</span>
                              <EditedLabel editedAt={post.edited_at} testId={`social-post-${post.post_id}-edited`} />
                              <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                                <Icon className="w-3 h-3" />{labelFor(post.category)}
                              </span>
                            </div>
                            <p className="text-[#6B4E8A] text-sm whitespace-pre-wrap">{post.content}</p>
                          </div>
                          <ContentActionsMenu
                              canManage={post.can_manage}
                              reportType="social_post"
                              reportId={post.post_id}
                              revealType={post.is_anonymous ? "social_post" : undefined}
                              revealId={post.post_id}
                              testIdPrefix={`social-post-${post.post_id}`}
                              onEdit={() => startEditPost(post)}
                              onDelete={() => handleDelete(post.post_id)}
                              deleteTitle="Delete this post?"
                              deleteDescription="This permanently removes the post and all of its comments. This cannot be undone."
                            />
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-4 mt-3 ml-13">
                          <button onClick={() => handleLike(post.post_id)} className={`flex items-center gap-1.5 text-sm transition-colors ${post.is_liked ? 'text-[#8DB5A0]' : 'text-[#64748B] hover:text-[#8DB5A0]'}`} data-testid={`like-${post.post_id}`}>
                            <Heart className={`w-4 h-4 ${post.is_liked ? 'fill-[#8DB5A0]' : ''}`} />
                            {post.like_count > 0 && post.like_count}
                          </button>
                          <button onClick={() => handleExpand(post.post_id)} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#6B4E8A]" data-testid={`comments-${post.post_id}`}>
                            <MessageCircle className="w-4 h-4" />
                            {post.comment_count > 0 && post.comment_count}
                          </button>
                        </div>
                      </div>
                      {/* Comments section */}
                      {isExpanded && (
                        <div className="border-t border-[#F0ECF7] bg-[#FAF7F2] p-4">
                          {(comments[post.post_id] || []).map(c => (
                            <div key={c.comment_id} className="mb-3 last:mb-0">
                              <ThreadItem
                                item={c}
                                idKey="comment_id"
                                reportType="social_comment"
                                testIdPrefix="comment"
                                variant="comment"
                                onSave={(item, content) => saveComment(post.post_id, item, content)}
                                onDelete={(item) => deleteComment(post.post_id, item)}
                              />
                            </div>
                          ))}
                          <div className="flex gap-2 mt-3">
                            <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Write a comment..." className="bg-white border-[#E5DFEC] text-sm h-9 flex-1" onKeyDown={e => e.key === "Enter" && handleComment(post.post_id)} data-testid={`comment-input-${post.post_id}`} />
                            <Button size="sm" onClick={() => handleComment(post.post_id)} className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full h-9 px-3" data-testid={`send-comment-${post.post_id}`}>
                              <Send className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {post.viewer_is_anonymous_op ? (
                            <p className="mt-2 text-xs text-[#64748B]" data-testid={`comment-forced-anon-${post.post_id}`}>This is your anonymous post — you'll comment as {post.author_name}.</p>
                          ) : (
                            <div className="mt-2 flex items-center gap-2">
                              <Checkbox id={`comment-anon-${post.post_id}`} checked={commentAnon} onCheckedChange={c => setCommentAnon(!!c)} data-testid={`comment-anonymous-checkbox-${post.post_id}`} />
                              <Label htmlFor={`comment-anon-${post.post_id}`} className="text-xs text-[#64748B] cursor-pointer">Comment anonymously</Label>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                <Heart className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No posts yet</h3>
                <p className="text-[#64748B] mb-4">{selectedCat ? "Be the first to post in this category!" : "Start the conversation!"}</p>
                <Button onClick={() => setShowCreate(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> New Post
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
                <button onClick={() => setSelectedCat("")} className={`px-4 py-2 text-sm rounded-full border transition-colors shrink-0 ${!selectedCat ? 'bg-[#6B4E8A] text-white border-[#6B4E8A]' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`}>All</button>
                {categories.map((cat) => {
                  const meta = catMetaFor(cat.key);
                  const Icon = meta.icon;
                  return (
                    <button key={cat.key} onClick={() => setSelectedCat(cat.key)} className={`px-4 py-2 text-sm rounded-full border transition-colors shrink-0 flex items-center gap-1.5 ${selectedCat === cat.key ? 'text-white border-transparent' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`} style={selectedCat === cat.key ? { backgroundColor: meta.color } : {}}>
                      <Icon className="w-3.5 h-3.5" />{cat.label}
                    </button>
                  );
                })}
              </div>
              <Dialog open={showCreateEvent} onOpenChange={setEventDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full shrink-0" data-testid="create-event-btn">
                    <CalendarDays className="w-4 h-4 mr-2" /> Create Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingEventId ? "Edit Community Event" : "Create Community Event"}</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                    <div><Label>Event Title *</Label><Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="e.g. Book Club: April Read" className="mt-2 bg-white border-[#E5DFEC]" data-testid="event-title-input" /></div>
                    <div><Label>Category</Label>
                      <Select value={eventForm.category} onValueChange={v => setEventForm({...eventForm, category: v})}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Date *</Label><Input type="date" value={eventForm.event_date} onChange={e => setEventForm({...eventForm, event_date: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="event-date-input" /></div>
                      <div><Label>Time</Label><Input type="time" value={eventForm.event_time} onChange={e => setEventForm({...eventForm, event_time: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="online-event" checked={eventForm.is_online} onCheckedChange={c => setEventForm({...eventForm, is_online: c, location: c ? "Online" : ""})} />
                      <Label htmlFor="online-event" className="cursor-pointer text-sm">Online event</Label>
                    </div>
                    {!eventForm.is_online && <div><Label>Location</Label><Input value={eventForm.location} onChange={e => setEventForm({...eventForm, location: e.target.value})} placeholder="Where?" className="mt-2 bg-white border-[#E5DFEC]" /></div>}
                    <div><Label>Max Attendees (optional)</Label><Input type="number" min="1" value={eventForm.max_attendees} onChange={e => setEventForm({...eventForm, max_attendees: e.target.value})} placeholder="Unlimited" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    <div><Label>Description</Label><Textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="What's the plan?" className="mt-2 bg-white border-[#E5DFEC]" data-testid="event-description-input" /></div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setEventDialog(false)} className="rounded-full">Cancel</Button>
                      <Button type="submit" className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full" disabled={submitting} data-testid="submit-event-btn">{submitting ? "Saving..." : editingEventId ? "Save changes" : "Create Event"}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {events.length > 0 ? (
              <div className="space-y-4">
                {events.map((event, idx) => {
                  const meta = catMetaFor(event.category);
                  const Icon = meta.icon;
                  const isAttending = event.attendees?.includes(user?.user_id);
                  const eventDate = new Date(event.event_date + (event.event_time ? `T${event.event_time}` : "T00:00"));
                  return (
                    <motion.div key={event.event_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 text-white" style={{ backgroundColor: meta.color }}>
                          <span className="text-xs font-medium opacity-70">{eventDate.toLocaleDateString("en-CA", { month: "short" })}</span>
                          <span className="text-lg font-bold leading-none">{eventDate.getDate()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</h3>
                            <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                              <Icon className="w-3 h-3" />{labelFor(event.category)}
                            </span>
                          </div>
                          {event.description && <p className="text-sm text-[#64748B] mb-2">{event.description}</p>}
                          <EditedLabel editedAt={event.edited_at} testId={`community-event-${event.event_id}-edited`} />
                          <div className="flex flex-wrap gap-3 text-xs text-[#64748B]">
                            {event.event_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.event_time}</span>}
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location || (event.is_online ? "Online" : "TBD")}</span>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{event.attendee_count} going{event.max_attendees ? ` / ${event.max_attendees} max` : ""}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleRSVP(event.event_id)}
                          className={`rounded-full shrink-0 ${isAttending ? 'bg-[#10B981] text-white hover:bg-[#059669]' : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'}`}
                          data-testid={`rsvp-${event.event_id}`}
                        >
                          {isAttending ? "Going" : "RSVP"}
                        </Button>
                        <ContentActionsMenu
                            canManage={event.can_manage}
                            reportType="community_event"
                            reportId={event.event_id}
                            testIdPrefix={`community-event-${event.event_id}`}
                            onEdit={() => startEditEvent(event)}
                            onDelete={() => handleDeleteEvent(event.event_id)}
                            deleteTitle="Delete this event?"
                            deleteDescription="This permanently removes the event and all RSVPs. This cannot be undone."
                          />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No upcoming events</h3>
                <p className="text-[#64748B] mb-4">Create a book club meetup, hiking group, or game night!</p>
                <Button onClick={() => setShowCreateEvent(true)} className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full">
                  <CalendarDays className="w-4 h-4 mr-2" /> Create Event
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Professional Feed — links to Discussions */}
          <TabsContent value="professional">
            <div className="bg-white border border-[#E5DFEC] rounded-2xl p-8 text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-[#6B4E8A]" />
              <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Professional Discussions</h3>
              <p className="text-[#64748B] mb-6 max-w-md mx-auto">Clinical topics, workplace questions, CE discussions, and professional advice live in the Discussions section.</p>
              <Link to="/discussions">
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full">
                  Go to Discussions <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Category Dialog */}
        <Dialog open={showCreateCategory} onOpenChange={setShowCreateCategory}>
          <DialogContent className="max-w-md" data-testid="create-category-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Tag className="w-5 h-5 text-[#8DB5A0]" />
                Create a New Category
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCategory} className="space-y-4 mt-4">
              <div>
                <Label>Category Name</Label>
                <Input
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  placeholder="e.g. Baking Club, Running Buddies..."
                  maxLength={40}
                  className="mt-2 bg-white border-[#E5DFEC]"
                  data-testid="new-category-input"
                  autoFocus
                />
                <p className="text-xs text-[#64748B] mt-1.5">{newCategoryName.length}/40 characters</p>
              </div>
              <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3 flex gap-2">
                <Sparkles className="w-4 h-4 text-[#8DB5A0] shrink-0 mt-0.5" />
                <p className="text-xs text-[#64748B]">New categories are shared with the whole community. Keep them fun, inclusive, and work-free!</p>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setShowCreateCategory(false); setNewCategoryName(""); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={creatingCategory || !newCategoryName.trim()} data-testid="submit-new-category">
                  {creatingCategory ? "Creating..." : "Create Category"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CommunityPage;
